const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";

let authToken = null;

export function setAuthToken(token) {
  authToken = token;
  if (token) localStorage.setItem("pl_token", token);
  else localStorage.removeItem("pl_token");
}

export function loadStoredToken() {
  authToken = localStorage.getItem("pl_token");
  return authToken;
}

export function getAuthToken() {
  return authToken;
}

async function request(path, options = {}, retries = 2, attempt = 0) {
  const { method = "GET", body, params } = options;
  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
    ).toString();
    if (qs) url += `?${qs}`;
  }

  // FormData (file uploads) must go out untouched so the browser sets the
  // multipart boundary itself.
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headers = isForm ? {} : { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });

    if (res.status === 401 && path !== "/auth/logout/") {
      window.dispatchEvent(new Event("pl:unauthorized"));
    }

    if (res.status === 204) return null;

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const message =
        data?.detail ||
        (data && typeof data === "object" ? Object.values(data).flat().join(" ") : null) ||
        `Request failed (${res.status})`;
      throw new Error(message);
    }
    return data;
  } catch (err) {
    // Only retry genuine network-layer failures (DNS, connection reset,
    // network change) -- never retry a real HTTP error response, since
    // that already threw a proper Error above with a real message.
    const isNetworkFailure = err instanceof TypeError || err.message === "Failed to fetch";
    if (isNetworkFailure && retries > 0) {
      const baseDelay = 500 * Math.pow(2, attempt);
      const jitter = Math.random() * 250;
      await new Promise((r) => setTimeout(r, baseDelay + jitter));
      return request(path, options, retries - 1, attempt + 1);
    }
    throw err;
  }
}


// Fetches a file with the auth header (a plain <a href> can't send it) and
// hands it to the browser as a download. Resolves to the saved file name.
async function download(path, params) {
  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
    ).toString();
    if (qs) url += `?${qs}`;
  }
  const headers = {};
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  const res = await fetch(url, { headers });
  if (res.status === 401) window.dispatchEvent(new Event("pl:unauthorized"));
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.detail || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const match = /filename="?([^";]+)"?/i.exec(res.headers.get("Content-Disposition") || "");
  const name = match ? match[1] : "export";

  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  return name;
}

export const api = {
  request,
  download,
  login: (username, password) => request("/auth/login/", { method: "POST", body: { username, password } }),
  logout: () => request("/auth/logout/", { method: "POST" }),

  dashboard: () => request("/dashboard/"),

  customers: (params) => request("/customers/", { params }),
  customer: (id) => request(`/customers/${id}/`),
  checkExistingCustomer: (email, phone) => request("/customers/check/", { params: { email, phone } }),
  createCustomer: (payload) => request("/customers/", { method: "POST", body: payload }),

  createPlan: (payload) => request("/plans/", { method: "POST", body: payload }),
  updatePlan: (id, payload) => request(`/plans/${id}/`, { method: "PATCH", body: payload }),
  completedPlans: () => request("/plans/completed/"),
  paymentHistory: () => request("/payments/history/"),
  markReceiptGenerated: (id) => request(`/plans/${id}/mark_receipt_generated/`, { method: "POST" }),
  updateCustomer: (id, payload) => request(`/customers/${id}/`, { method: "PATCH", body: payload }),
  editPayment: (id, payload) => request(`/payments/${id}/edit/`, { method: "PATCH", body: payload }),

  pendingPayments: () => request("/payments/", { params: { status: "pending" } }),
  confirmPayment: (id) => request(`/payments/${id}/confirm/`, { method: "POST" }),
  rejectPayment: (id, note) => request(`/payments/${id}/reject/`, { method: "POST", body: { note } }),
  revertPayment: (id) => request(`/payments/${id}/revert/`, { method: "POST" }),

  salesPeople: () => request("/sales-people/"),
  createSalesPerson: (name) => request("/sales-people/", { method: "POST", body: { name } }),
};